import { useCallback, useMemo, useState, useEffect } from 'react';

const throttle = (func, limit) => {
  let lastFunc;
  let lastRan;
  return (...args) => {
    const context = this;
    if (!lastRan) {
      func.apply(context, args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if (Date.now() - lastRan >= limit) {
          func.apply(context, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
};

const saveHeightToStorage = (key, height) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(key, height);
  }
};

const getHeightFromStorage = (key, defaultHeight) => {
  if (typeof window !== 'undefined') {
    const storedHeight = localStorage.getItem(key);
    return storedHeight ? parseInt(storedHeight, 10) : defaultHeight;
  }
  return defaultHeight;
};

export const usePromoBanner = (checkPromo = false, initialHeight = 0) => {
  const [promoHeight, setPromoHeight] = useState(() =>
    getHeightFromStorage('promoHeight', initialHeight),
  );

  const updatePromoHeight = useCallback(() => {
    const promoBanner = document.querySelector('.website-header--promo-bar');
    if (promoBanner) {
      const newHeight = promoBanner.getBoundingClientRect().height || initialHeight;
      if (newHeight !== promoHeight) {
        setPromoHeight(newHeight);
        saveHeightToStorage('promoHeight', newHeight);
      }
    } else {
      setPromoHeight(initialHeight);
    }
  }, [promoHeight, initialHeight]);

  const throttledUpdatePromoHeight = useMemo(
    () => throttle(updatePromoHeight, 100),
    [updatePromoHeight],
  );

  useEffect(() => {
    if (checkPromo) {
      throttledUpdatePromoHeight();

      const promoBannerContainer = document.querySelector('.website-header');
      const observer = new MutationObserver(throttledUpdatePromoHeight);
      if (promoBannerContainer) {
        observer.observe(promoBannerContainer, { childList: true, subtree: true });
      }

      return () => {
        if (promoBannerContainer) {
          observer.disconnect();
        }
      };
    }
  }, [checkPromo, throttledUpdatePromoHeight]);

  useEffect(() => {
    window.addEventListener('resize', throttledUpdatePromoHeight);
    return () => {
      window.removeEventListener('resize', throttledUpdatePromoHeight);
    };
  }, [throttledUpdatePromoHeight]);

  return promoHeight;
};

export const useHeaderHeight = (initialHeight = 135) => {
  const [height, setHeight] = useState(initialHeight);

  const updateHeight = useCallback(() => {
    const header = document.querySelector('.website-header');
    if (header) {
      const newHeight = header.getBoundingClientRect().height || initialHeight;
      if (newHeight !== height) {
        setHeight(newHeight);
        saveHeightToStorage('headerHeight', newHeight);
      }
    }
  }, [height, initialHeight]);

  const throttledUpdateHeight = useMemo(() => throttle(updateHeight, 50), [updateHeight]);

  useEffect(() => {
    const header = document.querySelector('.website-header');
    if (header) {
      throttledUpdateHeight();
    }
    window.addEventListener('resize', throttledUpdateHeight);

    const observer = new MutationObserver(throttledUpdateHeight);
    if (header) {
      observer.observe(header, { childList: true, subtree: true });
    }

    return () => {
      window.removeEventListener('resize', throttledUpdateHeight);
      if (header) {
        observer.disconnect();
      }
    };
  }, [throttledUpdateHeight]);

  useEffect(() => {
    const header = document.querySelector('.website-header');
    if (header) {
      setHeight(
        header.getBoundingClientRect().height ||
          getHeightFromStorage('headerHeight', initialHeight),
      );
    }
  }, []);

  return height;
};
